/**
 * Projection for `FleetHealthScene` — the demo's `renderFleet()` (line 2144),
 * expressed as data.
 *
 * Pure: no React, no fetching, no engine mutation. Every figure comes from
 * `shared/oem-engine.ts`; this file only groups, orders and labels.
 *
 * The demo renders six mine sections in `MINE_ORDER`, each holding division ›
 * subclass groups in a fixed division order, each holding one card per machine.
 * `fleetSections()` returns exactly that tree so the scene is a render loop
 * with no arithmetic in it.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/fleet/FleetHealthScene.tsx
 * @see docs/logic/fleet/fleet-health.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { FLEET, MINES, MINE_ORDER } from '../../data/core';
import type { ConditionBandSpec, Machine, MineCode } from '../../schema/machine';
import { ATTENTION_BANDS, bandOf } from '../../schema/machine';
import {
  bandsBestFirst,
  dataModeLabel,
  depthLabel,
  fleetGroups,
  fleetMineSummary,
  groupNum,
  machineCardData,
  statusOf,
  type DailyMetricCtx,
  type FleetMineSummary,
  type MachineCardData,
  type ShiftCtx,
} from '../../shared/oem-engine';

/** The data `FleetHealthScene` needs loaded before it can render cards. */
export type FleetSceneCtx = DailyMetricCtx & ShiftCtx;

/** One button on the demo's `.fhjump` bar (line 2145). */
export interface FleetJumpEntry {
  readonly mine: MineCode;
  readonly name: string;
  readonly machines: number;
  /** Machines in an attention band — the bold red count on the button. */
  readonly toWatch: number;
  /** DOM id of the section this button scrolls to. */
  readonly anchorId: string;
}

/** Demo line 2145. The jump bar, in `MINE_ORDER`. */
export const fleetJumpBar = (): FleetJumpEntry[] =>
  MINE_ORDER.map((mine) => {
    const machines = FLEET.filter((f) => f.mine === mine);
    return {
      mine,
      name: MINES[mine].name,
      machines: machines.length,
      toWatch: machines.filter((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id))).length,
      anchorId: sectionAnchorId(mine),
    };
  });

/** Demo line 2146. `id="mine-<code>"` — the scroll target of a jump button. */
export const sectionAnchorId = (mine: MineCode): string => `fleet-mine-${mine}`;

/** One division › subclass group, with its cards already derived. */
export interface FleetCardGroup {
  /** `divLabel(division) › subclass` — the eyebrow the demo prints. */
  readonly eyebrow: string;
  /** Stable key: division and subclass are unique within a mine. */
  readonly key: string;
  readonly count: number;
  readonly cards: readonly MachineCardData[];
}

/** One mine section of the fleet page. */
export interface FleetSection {
  readonly mine: MineCode;
  readonly name: string;
  readonly anchorId: string;
  /** `'WA · Underground · 1,150 m'`-style subtitle. */
  readonly detail: string;
  /** `dataModeLabel()` — `'Live · Wi-Fi/LTE'` or `'Shift-end · Pitram'`. */
  readonly dataMode: string;
  readonly isLive: boolean;
  /** Per-mine band counts and cost per operating hour. */
  readonly summary: FleetMineSummary;
  readonly groups: readonly FleetCardGroup[];
}

/**
 * Demo line 2144. The whole page as a tree.
 *
 * Expensive by construction — 100 machines × `machineCardData`, each of which
 * filters the 1,100-row daily-metric and shift tables. Call it inside a
 * `useMemo` keyed on the loaded payload, which is referentially stable.
 */
export function fleetSections(ctx: FleetSceneCtx): FleetSection[] {
  return MINE_ORDER.map((mine) => {
    const m = MINES[mine];
    return {
      mine,
      name: m.name,
      anchorId: sectionAnchorId(mine),
      detail: `${m.state} · ${depthLabel(m)}`,
      dataMode: dataModeLabel(m),
      isLive: m.data_mode === 'Live',
      summary: fleetMineSummary(mine),
      groups: fleetGroups(mine).map((g) => ({
        eyebrow: `${g.divisionLabel} › ${g.subclass}`,
        key: `${g.division}|${g.subclass}`,
        count: g.machines.length,
        cards: g.machines.map((f) => machineCardData(f, ctx)),
      })),
    };
  });
}

/** One segment of the `.bandbar` legend the demo prints under the page title. */
export interface BandLegendEntry {
  readonly band: ConditionBandSpec;
  readonly count: number;
  /** Share of the fleet, 0–100, for the stacked bar's flex weight. */
  readonly share: number;
}

/**
 * Demo line 2144 / 2836. `bandBar()` — the fleet-wide condition distribution,
 * good first. Bands with no machines are kept here and dropped by the renderer,
 * which is what lets the legend list every band while the bar shows only the
 * ones present.
 */
export function fleetBandLegend(): BandLegendEntry[] {
  const total = FLEET.length;
  return bandsBestFirst().map((band) => {
    const count = FLEET.filter((f) => statusOf(f.machine_id) === band.key).length;
    return { band, count, share: (count / total) * 100 };
  });
}

/**
 * Demo line 2131. `machineCard`'s meta line — model, drive/autonomy marker and
 * working, joined the demo's way.
 *
 * The `' (Spinifex)'` strip is the demo's: two Ochre Hill workings carry a
 * parenthetical that does not fit a dense card.
 */
export function machineMetaLine(f: Machine, hasWorkings: boolean): string {
  const drive = f.battery
    ? ' · battery'
    : f.autonomous
      ? ' · autonomous'
      : f.remote
        ? ' · tele-remote'
        : '';
  const working = f.working && hasWorkings ? ` · ${f.working.replace(' (Spinifex)', '')}` : '';
  return `${f.model}${drive}${working}`;
}

/** Demo line 2131. `${engine_hours.toLocaleString()} h`. */
export const engineHoursLabel = (f: Machine): string => `${groupNum(f.engine_hours)} h`;

/**
 * Demo line 2131. The card's bottom line: the worst attention-band component,
 * or the all-clear sentence.
 */
export function worstComponentLine(card: MachineCardData): string {
  const worst = card.worst;
  if (!worst) return 'All components ≥60/100';
  return `${worst.component} · ${worst.health_score}/100 ${bandOf(worst.health_score).label.toLowerCase()}`;
}
